// useReviewQueueCounts — lightweight pending-review tally for the
// always-mounted shell chrome (PRD #156).
//
// The sidebar "Review queue" badge and the agent-panel peek only need a
// number, not the full review-queue model. The hook polls
// `api.reviewQueue.counts()` (every 60s) and projects the payload to the
// caller's frontend via `selectFrontendCounts`, which falls back to zeros
// while loading or on error — so the always-mounted chrome never blocks
// navigation on a slow or failing endpoint.
//
// Each staff page mounts this hook, so the poll is slow on purpose. The poll
// was 10s, and the counts endpoint was then about one third of all API
// traffic in production. The poll stops while the tab is hidden. A review
// decision of the user refreshes the badge at once:
// `submitReviewDecisions` invalidates `REVIEW_QUEUE_COUNTS_KEY` after each
// write, and the review surfaces invalidate the `['review-queue']` prefix.
//
// The full `useReviewQueue` hook remains the source of truth for the
// review-queue page and the agent review surfaces.

import type {
    FrontendReviewCounts,
    ReviewQueueCountsResponse,
    AgentFrontend,
} from '@septima/contract';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface UseReviewQueueCountsResult {
    /** Number of review *items* for the frontend (post-call drafts collapse
     *  to one per call). Drives the sidebar badge. */
    itemCount: number;
    /** Number of individual approve/reject decisions those items carry. */
    decisionCount: number;
    /** True while the first counts fetch is in flight. */
    isLoading: boolean;
    /** True if the counts query errored — callers treat counts as zero. */
    isError: boolean;
}

/** The query key of the counts. It is under the `['review-queue']` prefix, so
 *  an invalidation of that prefix also refreshes the badge. */
export const REVIEW_QUEUE_COUNTS_KEY = ['review-queue', 'counts'] as const;

/** The poll interval of the badge, in milliseconds. */
export const REVIEW_QUEUE_COUNTS_POLL_MS = 60_000;

const ZERO: FrontendReviewCounts = { itemCount: 0, decisionCount: 0 };

/** Staff frontends the counts endpoint reports. `global` has no badge. */
function staffKeyOf(frontend?: AgentFrontend): keyof ReviewQueueCountsResponse | null {
    if (frontend === 'desk' || frontend === 'ops' || frontend === 'comply' || frontend === 'view') {
        return frontend;
    }
    return null;
}

/**
 * Project the counts payload down to a single frontend's tally. Pure, so
 * the empty (no data yet / errored) and missing-frontend fallbacks are
 * unit-testable without a DOM. Returns zeros when `data` is absent (loading
 * or error), when no frontend is given, or when the frontend is `global`.
 */
export function selectFrontendCounts(
    data: ReviewQueueCountsResponse | undefined,
    frontend?: AgentFrontend,
): FrontendReviewCounts {
    const key = staffKeyOf(frontend);
    if (!data || !key) return ZERO;
    return data[key];
}

export function useReviewQueueCounts(frontend?: AgentFrontend): UseReviewQueueCountsResult {
    const { data, isLoading, isError } = useQuery({
        queryKey: REVIEW_QUEUE_COUNTS_KEY,
        queryFn: async () => {
            const res = await api.reviewQueue.counts();
            if (res.status !== 200) {
                throw new Error(`review-queue counts fetch failed: ${res.status}`);
            }
            return res.body;
        },
        refetchInterval: REVIEW_QUEUE_COUNTS_POLL_MS,
        // This is the TanStack default. It is explicit here because the slow
        // poll depends on it: a hidden tab sends no request.
        refetchIntervalInBackground: false,
    });
    const counts = selectFrontendCounts(data, frontend);
    return {
        itemCount: counts.itemCount,
        decisionCount: counts.decisionCount,
        isLoading,
        isError,
    };
}
